import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError } from '../api';
import { useTenantCatalog } from '../hooks';
import { Modal } from '../components/Modal';
import { Icon } from '../shared/Icon';
import type { Aggregation } from '../types';

const AGGREGATIONS: Array<{ value: Aggregation; label: string; hint: string }> = [
  { value: 'COUNT', label: 'Количество событий', hint: 'осмотры, звонки, обращения' },
  { value: 'SUM', label: 'Сумма значения', hint: 'гигабайты, минуты — складывает свойство события' },
  { value: 'MAX', label: 'Максимум за период', hint: 'пиковое число мест или устройств' },
  { value: 'LATEST', label: 'Последнее значение', hint: 'объём хранилища на конец периода' },
  { value: 'UNIQUE_COUNT', label: 'Уникальные значения', hint: 'активные устройства, уникальные пациенты' },
];

/** Счётчики: что тенант измеряет и как события сворачиваются в тарифицируемое число. */
export function MetersPage() {
  const { tenantId = '' } = useParams();
  const queryClient = useQueryClient();
  const catalog = useTenantCatalog(tenantId);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    code: '',
    name: '',
    unit: 'шт',
    aggregation: 'COUNT' as Aggregation,
    valueProperty: '',
    filterJson: '',
  });

  const needsProperty = form.aggregation !== 'COUNT';

  const create = useMutation({
    mutationFn: () =>
      api.post(`/tenants/${tenantId}/meters`, {
        code: form.code,
        name: form.name,
        unit: form.unit,
        aggregation: form.aggregation,
        valueProperty: needsProperty ? form.valueProperty : undefined,
        filterJson: form.filterJson || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meters', tenantId] });
      setCreating(false);
    },
    onError: (err) => setError((err as ApiError).message),
  });

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Счётчики</h1>
          <div className="muted small">что измеряем и как считаем</div>
        </div>
        <div className="spacer" />
        <button className="btn btn-primary" onClick={() => { setError(null); setCreating(true); }}>
          <Icon name="plus" />
          Добавить
        </button>
      </div>

      {catalog.isLoading ? (
        <div className="state">Загрузка…</div>
      ) : catalog.meters.length === 0 ? (
        <div className="card">
          <div className="state-title">Счётчиков пока нет</div>
          <p className="muted">
            Счётчик описывает единицу потребления и способ её подсчёта. Тариф ссылается на счётчик,
            поэтому новый продукт добавляется настройкой, а не кодом.
          </p>
          <div className="state-actions">
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              <Icon name="plus" />
              Создать счётчик
            </button>
          </div>
        </div>
      ) : (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Код</th>
                <th>Название</th>
                <th>Единица</th>
                <th>Агрегация</th>
                <th>Фильтр</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {catalog.meters.map((meter) => (
                <tr key={meter.id}>
                  <td>
                    <code>{meter.code}</code>
                  </td>
                  <td>{meter.name}</td>
                  <td>{meter.unit}</td>
                  <td>
                    {AGGREGATIONS.find((a) => a.value === meter.aggregation)?.label ?? meter.aggregation}
                    {meter.valueProperty && (
                      <div className="muted small">по «{meter.valueProperty}»</div>
                    )}
                  </td>
                  <td className="muted small props">
                    {meter.filterJson ? <code>{meter.filterJson}</code> : 'без фильтра'}
                  </td>
                  <td>
                    {meter.active ? (
                      <span className="badge badge-green">активен</span>
                    ) : (
                      <span className="badge badge-gray">выключен</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <Modal title="Новый счётчик" onClose={() => setCreating(false)}>
          <div className="row">
            <div className="form-group">
              <label className="form-label" htmlFor="meter-code">
                Код
              </label>
              <input
                id="meter-code"
                className="input"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                autoFocus
              />
              <div className="form-hint">Его указывают в событиях потребления.</div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="meter-unit">
                Единица
              </label>
              <input
                id="meter-unit"
                className="input"
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="meter-name">
              Название
            </label>
            <input
              id="meter-name"
              className="input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="meter-agg">
              Как считать
            </label>
            <select
              id="meter-agg"
              className="input"
              value={form.aggregation}
              onChange={(e) => setForm({ ...form, aggregation: e.target.value as Aggregation })}
            >
              {AGGREGATIONS.map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </select>
            <div className="form-hint">{AGGREGATIONS.find((a) => a.value === form.aggregation)!.hint}</div>
          </div>

          {needsProperty && (
            <div className="form-group">
              <label className="form-label" htmlFor="meter-prop">
                Свойство события
              </label>
              <input
                id="meter-prop"
                className="input"
                value={form.valueProperty}
                onChange={(e) => setForm({ ...form, valueProperty: e.target.value })}
                placeholder="gigabytes"
              />
              <div className="form-hint">Из какого поля события брать значение.</div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label" htmlFor="meter-filter">
              Фильтр событий
            </label>
            <input
              id="meter-filter"
              className="input"
              value={form.filterJson}
              onChange={(e) => setForm({ ...form, filterJson: e.target.value })}
              placeholder={'{"kind":["PRE_TRIP_EXAM"]}'}
            />
            <div className="form-hint">
              Необязательно. Считаются только события, у которых перечисленные свойства совпали —
              так один поток событий кормит несколько счётчиков.
            </div>
          </div>

          {error && <div className="form-error">{error}</div>}

          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setCreating(false)}>
              Отмена
            </button>
            <button
              className="btn btn-primary"
              disabled={!form.code || !form.name || (needsProperty && !form.valueProperty) || create.isPending}
              onClick={() => { setError(null); create.mutate(); }}
            >
              Создать
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
